import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { selectCompanyPlan } from 'src/selectors/company.selector'
import { PlanEnum } from 'src/enums/planTypes'

/**
 * Funciones (nivel FE) que se muestran u ocultan según el plan de la empresa.
 * Los MÓDULOS se ocultan desde BE; este hook solo controla FUNCIONES en el FE.
 *
 * Para agregar una función nueva:
 *   1. Añade su key a `PlanFeature`.
 *   2. Añade su fila a `FEATURE_MATRIX` con los planes que la tienen habilitada.
 * No hace falta tocar los componentes consumidores.
 */
export type PlanFeature =
  | 'inventory'
  | 'pricingPolicies'
  | 'presentation'
  | 'recipes'
  | 'kardex'
  | 'lots'
  | 'stockMinMax'
  | 'reportsSegmented'
  | 'reportsByUser'

type PermissionPlanResponse = Record<PlanFeature, boolean>

/**
 * Planes activos que retorna /plans/available. Un plan fuera de esta lista se
 * considera legacy y muestra TODAS las funciones (default permisivo, para no
 * romper clientes existentes que aún tienen contratado un plan deprecado).
 */
const ACTIVE_PLANS: PlanEnum[] = [
  PlanEnum.FEL,
  PlanEnum.FREE,
  PlanEnum.POS_3,
  PlanEnum.CONTROL,
  PlanEnum.DIRECCION,
  PlanEnum.AGIL,
]

/**
 * Planes activos con acceso total: siempre muestran todas las funciones,
 * sin importar la whitelist por función.
 */
const FULL_ACCESS_PLANS: PlanEnum[] = [PlanEnum.POS_3]

/**
 * Whitelist por función: los planes que la tienen habilitada. Un plan activo
 * que NO esté en la lista de una función (ni en FULL_ACCESS_PLANS), la oculta.
 *
 * Las reglas reales de K2-3320 se van agregando una por una.
 */
const FEATURE_MATRIX: Record<PlanFeature, PlanEnum[]> = {
  inventory: [PlanEnum.CONTROL, PlanEnum.DIRECCION],
  pricingPolicies: [PlanEnum.DIRECCION],
  presentation: [PlanEnum.CONTROL, PlanEnum.DIRECCION],
  recipes: [PlanEnum.CONTROL, PlanEnum.DIRECCION],
  kardex: [PlanEnum.CONTROL, PlanEnum.DIRECCION],
  lots: [PlanEnum.DIRECCION],
  stockMinMax: [PlanEnum.CONTROL, PlanEnum.DIRECCION],
  reportsSegmented: [PlanEnum.CONTROL, PlanEnum.DIRECCION],
  reportsByUser: [PlanEnum.CONTROL, PlanEnum.DIRECCION],
}

const FEATURES = Object.keys(FEATURE_MATRIX) as PlanFeature[]

/** Nombre comercial de cada plan (para mensajes al usuario). */
const PLAN_LABELS: Partial<Record<PlanEnum, string>> = {
  [PlanEnum.FEL]: 'Kolo FEL',
  [PlanEnum.FREE]: 'KOLO Gratuito',
  [PlanEnum.POS_3]: 'POS 3 Usuarios',
  [PlanEnum.CONTROL]: 'KOLO CONTROL',
  [PlanEnum.DIRECCION]: 'KOLO DIRECCIÓN',
  [PlanEnum.AGIL]: 'KOLO ÁGIL',
}

/** Orden por capacidad (menor a mayor). Define cuál es el plan "mínimo". */
const PLAN_TIER_ORDER: PlanEnum[] = [
  PlanEnum.FREE,
  PlanEnum.FEL,
  PlanEnum.AGIL,
  PlanEnum.POS_3,
  PlanEnum.CONTROL,
  PlanEnum.DIRECCION,
]

/**
 * Nombres (ordenados por tier) de todos los planes que habilitan una función.
 * Sirve para mensajes del tipo "Disponible en los planes KOLO CONTROL y KOLO
 * DIRECCIÓN" en features bloqueadas.
 */
export const plansForFeature = (feature: PlanFeature): string[] =>
  PLAN_TIER_ORDER.filter(p => FEATURE_MATRIX[feature].some(fp => fp === p))
    .map(p => PLAN_LABELS[p] ?? '')
    .filter(Boolean)

export const usePermissionFromPlan = (): PermissionPlanResponse => {
  const plan: number = useSelector(selectCompanyPlan)

  return useMemo(() => {
    const isLegacy = !ACTIVE_PLANS.some(p => p === plan)
    const fullAccess = isLegacy || FULL_ACCESS_PLANS.some(p => p === plan)
    return FEATURES.reduce((acc, feature) => {
      acc[feature] = fullAccess || FEATURE_MATRIX[feature].some(p => p === plan)
      return acc
    }, {} as PermissionPlanResponse)
  }, [plan])
}
